import Box from "@mui/material/Box";

import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { ConsoleSurface } from "@/shared/ui";
import type { DeploymentAreaCapacity } from "@/features/deployment-settings/model/admin-settings";

interface DeploymentAreaCardProps {
  area: DeploymentAreaCapacity;
  isEditing: boolean;
  onChange: (value: number | "") => void;
}

export function DeploymentAreaCard({ area, isEditing, onChange }: DeploymentAreaCardProps) {
  return (
    <ConsoleSurface sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box>
            <Typography component="h3" variant="subtitle1">
              {area.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Area
            </Typography>
          </Box>
        </Box>
        {isEditing ? (
          <TextField
            label="Capacity"
            type="number"
            value={area.capacity}
            onChange={(event) => {
              const rawValue = event.target.value;
              onChange(rawValue === "" ? "" : Number(rawValue));
            }}
            slotProps={{
              htmlInput: { min: 0, step: 1 },
              input: {
                endAdornment: <InputAdornment position="end">people</InputAdornment>,
              },
            }}
            helperText={
              area.capacity === ""
                ? "Empty capacity will leave this area without a capacity configuration."
                : "Used to resolve population percentage thresholds."
            }
            fullWidth
          />
        ) : (
          <Box
            sx={(theme) => ({
              p: 1.5,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: alpha(theme.palette.common.white, 0.035),
              border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
            })}
          >
            <Typography variant="caption" color="text.secondary">
              Capacity
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>
              {area.capacity === "" ? "Not configured" : `${area.capacity} people`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {area.capacity === ""
                ? "Capacity-based thresholds cannot resolve for this area."
                : "Available to percentage-of-capacity thresholds."}
            </Typography>
          </Box>
        )}
      </Stack>
    </ConsoleSurface>
  );
}
